import {
  BookmarkSimple,
  Calendar,
  CalendarCheck,
  ChatCircle,
  CheckCircle,
  Clock,
  DotsThreeVertical,
  Eye,
  EyeSlash,
  Flag,
  Globe,
  LinkSimple,
  MapPin,
  PaperPlaneTilt,
  QrCode,
  SlidersHorizontal,
  Star,
  Users,
  UserSound,
  UsersThree,
} from '@phosphor-icons/react';
import { EventEnLigne, EventEtat, EventSpontane } from '../../components/ui/EventEtat';
import { type Sanction, type SiroccoEvent } from '@sirocco/shared';
import {
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { events, messages } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { Button, IconButton } from '../../components/ui/Button';
import { ConfirmDialog, useConfirm } from '../../components/ui/Confirm';
import { Popover } from '../../components/ui/Popover';
import { PartageSheet } from '../../components/ui/PartageSheet';
import { Sheet, SheetAction } from '../../components/ui/Sheet';
import { reportPath } from '../report/ReportScreen';
import { useFlashOnArrival } from '../../utils/flash';
import { lienDEvenement } from '../../utils/partage';
import { useAsync } from '../../hooks/useAsync';
import { useSession } from '../../store/SessionContext';
import { shortDate, timeRange } from '../../utils/format';
import { billetEnSvg } from '../../utils/billet';
import { itineraires } from '../../utils/adresses';
import { hoteDuLien } from '../../utils/lienEnLigne';
import { profilePath } from '../../utils/routes';
import { locale, t } from '../../i18n';
import { ficheDe } from '../../utils/langues';

/* La carte du lieu, et sa bibliotheque : chargees seulement pour une fiche qui a un point. */
const ApercuDuLieu = lazy(() =>
  import('./carte/ApercuDuLieu').then((module) => ({ default: module.ApercuDuLieu })),
);

/**
 * Fiche d'un evenement.
 *
 * Le bouton du bas porte quatre etats : demander, annuler sa demande,
 * se desinscrire, ou gerer pour l'organisateur. Apres chaque action on
 * recharge, car la base decide aussi bien du statut que de la liste des
 * participants.
 */
export function EventDetailScreen() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, server, ambiance } = useSession();
  /*
    La meme cle que l'ecran de gestion et que la carte d'un post : un
    evenement est le meme partout, et cette cle est ce qui evite le
    chargement au retour depuis « Gerer ». Les actions faites la-bas y
    ecrivent aussi, donc on revient sur l'etat a jour, pas sur l'ancien.
  */
  const { data, loading, error, reload, setData } = useAsync(
    () => events.get(eventId ?? ''),
    [eventId],
    `event:${eventId ?? ''}`,
  );

  const [menuOpen, setMenuOpen] = useState(false);
  /* Le bouton d'en-tete d'ou le menu sort. */
  const menuAnchor = useRef<HTMLButtonElement>(null);
  /*
    Le lieu est le seul repere qui puisse etre long : une adresse tient
    rarement dans une demi-largeur. Il s'ouvre donc en fenetre, accrochee
    a lui, plutot que de rester coupe par des points de suspension.
  */
  const lieuAnchor = useRef<HTMLButtonElement>(null);
  const [lieuOuvert, setLieuOuvert] = useState(false);
  /*
    Une adresse trop longue pour sa demi-largeur prend toute la ligne, et
    les places descendent a cote des langues : elle se lit en entier au
    lieu de finir par trois points.
  */
  const [lieuLong, setLieuLong] = useState(false);
  const [billetOpen, setBilletOpen] = useState(false);
  /*
    Ma place dans le groupe de la sortie.

    `null` tant qu'on ne sait pas : le bouton ne doit pas clignoter d'un
    libelle a l'autre pendant la lecture.
  */
  const [dansLeGroupe, setDansLeGroupe] = useState<boolean | null>(null);
  const [partageOuvert, setPartageOuvert] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  /*
    Ce que la feuille de partage a fait.

    Distinct de `notice`, qui s'affiche en rouge en bas de la fiche et y
    reste : un envoi reussi n'est pas une erreur. Meme pastille ephemere
    qu'ailleurs dans l'application.
  */
  const [toast, setToast] = useState<string | null>(null);
  /* Le retour d'un ecran de signalement, avec son accuse de reception. */
  useFlashOnArrival(setNotice);
  const [busy, setBusy] = useState(false);
  // Bloquer l'organisateur casse l'amitie et vide son profil : on confirme.
  const { pending, request, cancel, confirm } = useConfirm();

  /**
   * Ma presence, telle que l'organisateur l'a constatee.
   *
   * Elle se decidait entierement de son cote : billet scanne ou case
   * cochee, rien ne revenait a celui qui etait passe — alors que c'est
   * cette marque, et non l'inscription, qui fait compter l'evenement dans
   * les trophees. Peu importe par quel moyen elle a ete posee : ce qui se
   * dit ici, c'est qu'elle l'est.
   *
   * Lue a part de l'evenement, comme dans l'ecran de gestion : une fiche
   * n'a pas a porter la liste des presences pour en montrer une seule.
   */
  const [maPresence, setMaPresence] = useState(false);

  useEffect(() => {
    const groupId = data?.groupId ?? null;
    if (!groupId) return setDansLeGroupe(null);
    let vivant = true;
    void events
      .dansLeGroupe(groupId)
      .then((dedans: boolean) => vivant && setDansLeGroupe(dedans))
      .catch(() => vivant && setDansLeGroupe(null));
    return () => {
      vivant = false;
    };
  }, [data?.groupId]);
  const myId = user?.id;
  const suisConfirme = data?.myStatus === 'confirmed' && data.organizer.id !== myId;

  useEffect(() => {
    if (!eventId || !myId || !suisConfirme) return setMaPresence(false);
    let vivant = true;
    void events
      .attendance(eventId)
      .then((marques) => vivant && setMaPresence(Boolean(marques[myId])))
      // Un echec ne dit rien de faux : on n'affiche simplement pas la mention.
      .catch(() => undefined);
    return () => {
      vivant = false;
    };
  }, [eventId, myId, suisConfirme]);

  /**
   * La note que j'ai donnee a l'organisateur, s'il y en a une.
   *
   * Elle ne se demande qu'a qui y etait : la fenetre s'ouvre quand
   * l'evenement est passe et que ma presence a ete constatee. C'est ce
   * qui fait qu'une note vient de quelqu'un qui y etait.
   */
  const [maNote, setMaNote] = useState<number | null>(null);
  const [notation, setNotation] = useState(false);
  const peutNoter = Boolean(data?.isPast) && maPresence;

  useEffect(() => {
    if (!data || !peutNoter) return setMaNote(null);
    let vivant = true;
    void events
      .myRating(data)
      .then((note) => vivant && setMaNote(note))
      .catch(() => undefined);
    return () => {
      vivant = false;
    };
  }, [data, peutNoter]);

  /**
   * Ma sanction, s'il y en a une.
   *
   * Lue seulement quand la question se pose — un evenement a venir
   * auquel je ne suis pas inscrit. Ailleurs, elle n'a rien a dire.
   */
  const [sanction, setSanction] = useState<Sanction | null>(null);
  const peutSInscrire =
    Boolean(data) && !data?.isPast && data?.statut !== 'annule' && data?.myStatus === 'none';

  useEffect(() => {
    if (!peutSInscrire) return setSanction(null);
    let vivant = true;
    void events
      .sanction()
      .then((etat) => vivant && setSanction(etat))
      .catch(() => undefined);
    return () => {
      vivant = false;
    };
  }, [peutSInscrire]);

  const suspenduJusqua = sanction?.jusqua ?? null;

  async function noter(note: number) {
    if (!data) return;
    setNotation(true);
    setNotice(null);
    try {
      await events.rate(data, note);
      setMaNote(note);
      /* La moyenne affichee vient de la fiche : on la relit pour que la
         note qu'on vient de donner y compte tout de suite. */
      reload();
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : t.sorties.fiche.noteNonEnvoyee);
    } finally {
      setNotation(false);
    }
  }

  /**
   * Ouvre le groupe de la sortie, en y rentrant s'il le faut.
   *
   * Le retour precede la navigation : arriver dans une conversation dont
   * on n'est pas membre ne montre rien, les regles refusant jusqu'a la
   * lecture du fil.
   */
  async function ouvrirLeGroupe() {
    if (!data?.groupId) return;
    setBusy(true);
    try {
      if (dansLeGroupe === false) {
        await events.rejoindreLeGroupe(data);
        setDansLeGroupe(true);
      }
      navigate(`/app/messages/${data.groupId}`);
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : t.sorties.fiche.groupeImpossible);
    } finally {
      setBusy(false);
    }
  }

  async function onBookmark() {
    if (!data) return;
    // Le signet ne change rien d'autre : on prend la reponse telle quelle.
    setData(await events.bookmark(data));
  }

  /**
   * Ecrire a l'organisateur.
   *
   * C'est la seule porte vers un message prive sans etre amis : elle
   * reste ouverte tant que l'evenement est au programme. Un refus se dit
   * — il arrive quand l'evenement est passe et qu'on n'est pas amis, et
   * un bouton muet laisserait croire a une panne.
   */
  async function onOpenMessage() {
    if (!data) return;
    setNotice(null);
    try {
      const conversation = await messages.openDirect(data.organizer.id);
      navigate(`/app/messages/${conversation.id}`);
    } catch (cause) {
      setNotice(
        cause instanceof Error ? cause.message : t.sorties.fiche.ecrireImpossible,
      );
    }
  }

  /**
   * Demander, annuler sa demande, ou se desinscrire.
   * Une demande en attente s'annule comme une inscription : la retirer,
   * c'est effacer sa ligne de la liste des participations.
   *
   * Le refus est dit, pas avale : un creneau deja pris, un evenement
   * complet, et le bouton ne bougerait pas sans un mot d'explication.
   */
  async function participer(sortie: SiroccoEvent) {
    setNotice(null);
    setBusy(true);
    try {
      if (sortie.myStatus === 'confirmed' || sortie.myStatus === 'pending') await events.leave(sortie);
      else await events.join(sortie);
      reload();
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : t.sorties.commun.actionImpossible);
    } finally {
      setBusy(false);
    }
  }

  /*
    Se desinscrire se confirme, s'inscrire non.

    La place liberee peut etre prise dans la minute, et l'organisateur
    est prevenu du depart : un doigt qui glisse ne doit pas suffire. Une
    demande en attente, elle, s'annule sans question — rien n'est encore
    engage de part et d'autre.
  */
  function onParticipation() {
    if (!data) return;
    if (data.myStatus === 'confirmed') {
      request(t.sorties.fiche.desinscrireQ, () => void participer(data));
      return;
    }
    void participer(data);
  }

  const isOrganizer = Boolean(data && user && data.organizer.id === user.id);
  // La liste nominative n'est pas publique : elle s'ouvre a ceux qui font
  // partie de l'evenement, pas aux curieux de passage.
  const seesRoster = isOrganizer || data?.myStatus === 'confirmed';

  function closeMenu() {
    setMenuOpen(false);
  }

  /** Referme la feuille, puis pose la question au centre de l'ecran. */
  function ask(question: string, action: () => void) {
    closeMenu();
    request(question, action);
  }

  /**
   * Masquer, signaler, bloquer : chacune de ces actions retire
   * l'evenement de ma liste, donc on quitte la fiche derriere.
   */
  async function run(action: () => Promise<unknown>) {
    closeMenu();
    setBusy(true);
    try {
      await action();
      navigate('/app/events', { replace: true });
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : t.sorties.commun.actionImpossible);
      setBusy(false);
    }
  }

  return (
    <SubScreen
      title={t.sorties.fiche.titre}
      action={
        data && (
          <>
            <IconButton
              label={data.bookmarked ? t.fil.commun.retirerSignet : t.fil.commun.enregistrer}
              variant="ghost"
              className="ev-entete__icone"
              size={34}
              onClick={onBookmark}
            >
              <BookmarkSimple size={22} weight={data.bookmarked ? 'fill' : 'regular'} />
            </IconButton>
            {/*
              Le menu, pour tout le monde : partager y vit desormais, et
              l'organisateur est le premier a vouloir faire connaitre sa
              sortie. Il n'y trouve que cela — masquer ou signaler sa propre
              sortie n'aurait pas de sens.

              Partager quittait l'en-tete : son avion de papier y cotoyait
              celui qui ecrit a l'organisateur, et deux fois le meme dessin
              pour deux gestes differents, c'etait un de trop.
            */}
            <IconButton
              label={t.sorties.commun.autresActions}
              variant="ghost"
              className="ev-entete__icone"
              size={34}
              ref={menuAnchor}
              onClick={() => setMenuOpen(true)}
            >
              <DotsThreeVertical size={22} weight="bold" />
            </IconButton>
          </>
        )
      }
      overlay={
        data && (
          <>
            {/*
              L'adresse en entier, sortie du repere qui la porte. Meme
              fenetre que la cloche du fil : le fond se fige, s'assombrit
              et se brouille, si bien qu'il ne reste que ce qu'on est venu
              lire.
            */}
            <Popover
              open={lieuOuvert}
              anchor={lieuAnchor}
              onClose={() => setLieuOuvert(false)}
              label={t.sorties.fiche.adresseComplete}
              /*
                `large` pour le voile, pas pour la largeur : c'est lui qui
                fait le fond sombre et flou de la cloche. Un menu de trois
                lignes laisse voir ce qu'il commande ; une adresse qu'on
                vient lire, non — la page derriere n'a plus a se lire, et
                sans cela le texte du dessous se melait au sien.
              */
              large
            >
              <div className="ev-lieu">
                <p className="ev-lieu__label">{t.sorties.commun.lieu}</p>
                <p className="ev-lieu__value">{data.location}</p>

                {/*
                  Trois applications, et non une seule : personne n'utilise
                  la meme. Plans est toujours la sur un iPhone, Google Maps
                  et Waze seulement si on les a — mais leurs liens basculent
                  vers l'application quand elle existe et ouvrent le site
                  sinon, donc aucun ne peut echouer.

                  L'itineraire part du point de l'adresse quand on l'a ;
                  des sorties d'avant l'annuaire n'ont qu'un texte, et il
                  sert alors de destination — moins sur, mais mieux que
                  rien.
                */}
                <ul className="ev-lieu__routes">
                  {itineraires(data.location, data.lat, data.lon).map((route) => (
                    <li key={route.nom}>
                      <a
                        className="ev-lieu__route"
                        href={route.lien}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img className="ev-lieu__logo" src={route.logo} alt="" />
                        {route.nom}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </Popover>

            {/* Sans titre : la fiche ne parle que de cet evenement. */}
            {/*
              Ce qui part a un ami, c'est l'evenement lui-meme : la carte
              se lit dans la conversation, avec le lieu et l'heure du jour.
              Le lien, lui, ne sert qu'a ceux qui sont dehors.
            */}
            <PartageSheet
              open={partageOuvert}
              anchor={menuAnchor}
              onClose={() => setPartageOuvert(false)}
              quoi={{ eventId: data.id }}
              lien={lienDEvenement(data.id)}
              texte={data.title}
              onFait={(message) => {
                setToast(message);
                setTimeout(() => setToast(null), 2600);
              }}
            />

            {toast && <p className="feed__toast">{toast}</p>}

            <Sheet anchor={menuAnchor} open={menuOpen} onClose={closeMenu}>
              {/* La feuille de partage prend la suite du menu, qui se referme. */}
              <SheetAction
                icon={<PaperPlaneTilt className="sens-x" size={22} />}
                onClick={() => {
                  closeMenu();
                  setPartageOuvert(true);
                }}
              >
                {t.sorties.fiche.partager}
              </SheetAction>

              {/*
                Masquer et demasquer sont la meme porte, dans les deux sens.
                Remettre en circulation ne demande pas de confirmation : le
                geste ne coute rien et se refait aussitot.
              */}
              {isOrganizer ? null : data.hidden ? (
                <SheetAction
                  icon={<Eye size={22} />}
                  onClick={() => void run(() => events.unhide(data.id))}
                >
                  {t.sorties.fiche.retirerMasques}
                </SheetAction>
              ) : (
                <SheetAction
                  icon={<EyeSlash size={22} />}
                  onClick={() =>
                    ask(t.sorties.commun.masquerQ, () => void run(() => events.hide(data.id, data)))
                  }
                >
                  {t.sorties.commun.masquer}
                </SheetAction>
              )}

              {/* Le motif et le detail se choisissent sur leur propre ecran. */}
              {!isOrganizer && (
                <SheetAction
                  icon={<Flag size={22} />}
                  onClick={() => navigate(reportPath('event', data.id, { serverId: data.serverId }))}
                >
                  {t.fil.commun.signaler}
                </SheetAction>
              )}
            </Sheet>

            {/*
              Le billet, en grand et sur fond clair.

              Il vit dans une feuille plutot que dans la fiche : on le
              sort au moment de passer, on le referme apres. Le laisser
              en permanence dans la page ferait descendre tout le reste
              pour un carre qui ne sert qu'une minute.
            */}
            <Sheet
              open={billetOpen}
              large
              onClose={() => setBilletOpen(false)}
              title={t.sorties.commun.monBillet}
            >
              <div className="ev-billet">
                <div
                  className="ev-billet__code"
                  dangerouslySetInnerHTML={{
                    __html: billetEnSvg({ eventId: data.id, userId: user?.id ?? '' }),
                  }}
                />
                <p className="ev-billet__hint">
                  {maPresence
                    ? t.sorties.fiche.presenceEnregistree
                    : t.sorties.fiche.montreLeCarre}
                </p>
              </div>
            </Sheet>

            <ConfirmDialog pending={pending} onCancel={cancel} onConfirm={confirm} />
          </>
        )
      }
      bottomBar={
        data && (
          <div className="ev-bottom">
            {/*
              Un evenement termine ne se touche plus : ni gestion, ni
              inscription, ni retrait. Il reste consultable — c'est la
              trace de ce qui a eu lieu, et les trophees s'appuient
              dessus.
            */}
            {data.isPast ? (
              <p className="ev-bottom__over">{t.sorties.fiche.passe}</p>
            ) : data.statut === 'annule' && !isOrganizer ? (
              /* Le compte n'y etait pas a l'heure dite : plus rien a faire. */
              <p className="ev-bottom__over">{t.composants.etat.annule}</p>
            ) : isOrganizer ? (
              <Button
                icon={<SlidersHorizontal size={20} />}
                disabled={busy}
                onClick={() => navigate(`/app/events/${data.id}/manage`)}
              >
                {data.pendingParticipants.length > 0
                  ? t.sorties.fiche.gererDemandes(data.pendingParticipants.length)
                  : t.sorties.fiche.gerer}
              </Button>
            ) : suspenduJusqua ? (
              /*
                Suspendu : le bouton ne ment pas. Il dit la raison et la
                date, plutot que de laisser appuyer pour un refus.
              */
              <p className="ev-bottom__over ev-bottom__suspendu">
                {t.sorties.fiche.suspendu(suspenduJusqua)}
              </p>
            ) : (
              <ParticipationButton
                status={data.myStatus}
                full={data.participants.length >= data.maxParticipants}
                validation={data.requiresValidation}
                disabled={busy}
                onClick={onParticipation}
              />
            )}
            {/*
              Le billet n'existe que pour qui est accepte et avant que ce
              soit passe : une demande en attente n'ouvre aucune porte, et
              un evenement termine n'a plus d'entree.
            */}
            {/* En ligne, pas de porte a passer : la presence se coche a la main. */}
            {!data.isPast && !isOrganizer && data.myStatus === 'confirmed' && !data.enLigne && (
              <IconButton label={t.sorties.commun.monBillet} onClick={() => setBilletOpen(true)} size={52}>
                <QrCode size={20} />
              </IconButton>
            )}
            {/*
              Le groupe de la sortie, a la place de l'agenda.

              L'agenda est a un onglet d'ici ; le groupe, lui, ne se
              trouve qu'en fouillant ses conversations. C'est le raccourci
              qui manquait, et il n'a de sens que pour qui participe.

              On peut avoir quitte le groupe sans quitter la sortie : le
              bouton y ramene alors en y rentrant d'abord. C'est le seul
              chemin de retour — on n'entre pas dans un groupe de sortie
              sur invitation.
            */}
            {!data.isPast && data.groupId && (isOrganizer || data.myStatus === 'confirmed') ? (
              <IconButton
                label={dansLeGroupe === false ? t.sorties.fiche.rejoindreGroupe : t.sorties.fiche.groupe}
                disabled={busy}
                onClick={() => void ouvrirLeGroupe()}
                size={52}
              >
                <UsersThree size={20} />
              </IconButton>
            ) : (
              <IconButton
                label={t.sorties.fiche.monAgenda}
                onClick={() => navigate('/app/events/agenda')}
                size={52}
              >
                <CalendarCheck size={20} />
              </IconButton>
            )}
          </div>
        )
      }
    >
      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}

      {data && (
        <div className="ev-detail">
          {/*
            La categorie a rejoint le titre, a sa droite : posee au-dessus,
            elle occupait une ligne entiere pour un mot, et l'on lisait la
            categorie avant de savoir de quoi il s'agissait.
          */}
          <div className="ev-detail__chips">
            {/* Ce que l'organisateur a constate, rendu a qui est passe. */}
            {maPresence && (
              <span className="ev-chip ev-chip--present">
                <CheckCircle size={16} weight="fill" />
                {t.sorties.fiche.presenceConfirmee}
              </span>
            )}

            {/*
              Le seuil et la sortie spontanee, avec les pastilles plutot
              qu'en pied de fiche : c'est avant de lire le detail qu'on
              veut savoir si le rendez-vous tient.
            */}
            <EventSpontane event={data} />
            <EventEnLigne event={data} />
            {/* L'association qui l'organise : on rejoint sa page d'un toucher. */}
            {data.asso && server?.associations?.[data.asso] && (
              <Link to={`/app/annuaire/associations/${data.asso}`} className="ev-asso">
                <UsersThree size={13} />
                {server.associations[data.asso].nom}
              </Link>
            )}
            <EventEtat event={data} />
          </div>

          <div className="ev-detail__head">
            <h2 className="ev-detail__title">{data.title}</h2>
            <span className="ev-chip">{t.sorties.categories[data.category]}</span>
          </div>

          {/* Ni carte ni bordure : l'organisateur pose sur le fond de l'ecran. */}
          <div className="ev-organizer">
            <Avatar user={data.organizer} size={44} />
            <span className="ev-organizer__body">
              <span className="ev-organizer__name">
                {t.sorties.fiche.organisePar(data.organizer.username)}
              </span>
              <span className="ev-organizer__meta">
                {/* La note d'abord : c'est elle qu'on cherche avant de
                    s'inscrire chez quelqu'un qu'on ne connait pas. */}
                {data.organizerRating && (
                  <>
                    {/* L'etoile et le chiffre ne se separent pas : la ligne
                        pouvait passer entre les deux, et la note se lisait
                        alors sur deux etages. */}
                    <span className="ev-organizer__note">
                      <Star size={13} weight="fill" className="ev-organizer__etoile" />
                      {data.organizerRating.moyenne.toLocaleString(locale)}
                    </span>
                  </>
                )}
              </span>
            </span>
            {/* Une bulle, pas l'avion de papier : ecrire n'est pas partager. */}
            <IconButton label={t.sorties.fiche.ecrireA(data.organizer.username)} onClick={onOpenMessage}>
              <ChatCircle size={20} />
            </IconButton>
          </div>

          <div className="ev-facts">
            <Fact icon={<Calendar size={16} />} label={t.sorties.commun.date} value={shortDate(data.date)} />
            <Fact
              icon={<Clock size={16} />}
              label={t.sorties.fiche.horaire}
              value={timeRange(data.startTime, data.endTime)}
            />
            {data.enLigne && !data.lien ? (
              /* En ligne sans lien : une partie de console, on se retrouve chacun chez soi. */
              <Fact icon={<Globe size={16} />} label={t.sorties.commun.lieu} value={data.location} />
            ) : data.enLigne ? (
              <Fact
                icon={<LinkSimple size={16} />}
                label={t.sorties.fiche.lien}
                /*
                  Le lien a l'organisateur et aux inscrits acceptes, comme
                  le groupe de la sortie : une demande en attente n'ouvre
                  pas la porte. Il s'ouvre dans l'application qui le sert
                  (Zoom, Teams, Discord) quand elle est la.
                */
                value={
                  data.lien && (isOrganizer || data.myStatus === 'confirmed') ? (
                    <a
                      className="ev-fact__lien"
                      href={data.lien}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={t.sorties.fiche.rejoindre(hoteDuLien(data.lien))}
                    >
                      {hoteDuLien(data.lien)}
                    </a>
                  ) : (
                    t.sorties.fiche.lienApresInscription
                  )
                }
              />
            ) : (
              <FactLieu
                icon={<MapPin size={16} />}
                value={data.location}
                anchor={lieuAnchor}
                long={lieuLong}
                onLong={setLieuLong}
                onOpen={() => setLieuOuvert(true)}
              />
            )}
            <Fact
              icon={<Users size={16} />}
              label={t.sorties.fiche.places}
              value={`${data.participants.length} / ${data.maxParticipants}`}
            />
            <Fact
              icon={<UserSound size={16} />}
              label={t.sorties.fiche.langues}
              /*
                Chaque nom dans son propre isolat : deux langues ecrites de
                droite a gauche, l'une apres l'autre, se seraient fondues en
                un seul passage arabe, virgules et ordre inverses.
              */
              value={data.langues.map((code, rang) => (
                <span key={code}>
                  {rang > 0 && ', '}
                  <bdi lang={code}>{ficheDe(code).nom}</bdi>
                </span>
              ))}
              /* Toutes les langues, jamais « … » : c'est la qu'on lit si l'on s'y comprendra. */
              entier
              /* A cote des places, quand l'adresse a pris leur ligne. */
              demi={lieuLong}
            />
          </div>

          {/*
            Noter l'organisateur.

            Pose ici, juste sous les reperes : c'est la premiere chose
            qu'on voit en rouvrant la fiche d'un evenement auquel on est
            alle. Il n'apparait que pour qui y etait, et disparait de
            lui-meme une fois la note donnee — remplace par ce qu'on a
            mis, sans possibilite de le reprendre.
          */}
          {peutNoter && (
            <div className="card ev-note">
              <p className="ev-note__titre">
                {maNote ? t.sorties.fiche.noteOrganisateur : t.sorties.fiche.commentCaSEstPasse}
              </p>
              {/* Une note deja donnee se lit sur les etoiles : rien a
                  expliquer de plus. La phrase ne sert qu'avant. */}
              {!maNote && (
                <p className="ev-note__aide">
                  {t.sorties.fiche.noteAide(data.organizer.username)}
                </p>
              )}

              <div
                className={`ev-note__etoiles${maNote ? ' ev-note__etoiles--figee' : ''}`}
                role="group"
                aria-label={t.sorties.fiche.noteSurCinq}
              >
                {[1, 2, 3, 4, 5].map((valeur) => {
                  const pleine = maNote !== null && valeur <= maNote;
                  return (
                    <button
                      key={valeur}
                      type="button"
                      className={`ev-note__etoile${pleine ? ' ev-note__etoile--on' : ''}`}
                      aria-label={t.sorties.fiche.surCinq(valeur)}
                      aria-pressed={pleine}
                      disabled={maNote !== null || notation}
                      /*
                        La note passe par une question.

                        Elle ne se reprend pas, et cinq cibles cote a cote
                        se touchent facilement de travers : mieux vaut une
                        seconde de plus qu'une etoile qui suit quelqu'un
                        pour toujours.
                      */
                      onClick={() =>
                        request(
                          t.sorties.fiche.donnerQ(valeur, data.organizer.username),
                          () => void noter(valeur),
                        )
                      }
                    >
                      <Star size={30} weight={pleine ? 'fill' : 'regular'} />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Un titre sans texte dessous ne dit rien : les deux vont ensemble. */}
          {data.description && (
            <>
              <h3 className="ev-heading">{t.sorties.fiche.description}</h3>
              <p className="ev-description">{data.description}</p>
            </>
          )}

          {/*
            Ou c'est, sans quitter la fiche : entre ce qu'on sait de
            l'evenement et ceux qui y vont. Rien s'il n'a pas de point,
            comme un lieu du campus ecrit sans etre place.
          */}
          {data.lat !== null && data.lon !== null && (
            <Suspense fallback={<div className="ev-apercu" />}>
              <ApercuDuLieu
                sortie={{ ...data, lat: data.lat, lon: data.lon }}
                nuit={ambiance === 'night'}
                campus={server?.campus === true}
                centreDuCampus={server?.centre}
              />
            </Suspense>
          )}

          <h3 className="ev-heading">
            {t.sorties.commun.participants}
            <span className="ev-heading__compte">{data.participants.length}</span>
          </h3>

          {/*
            Tant qu'on n'est pas accepte, la liste se resume a son
            organisateur — lui s'affiche deja en haut de la fiche, il n'y a
            rien a proteger. La pile d'avatars montrait les visages de tous
            les participants a n'importe quel curieux de passage : elle
            annoncait l'anonymat et livrait le contraire.
          */}
          <div className="ev-roster">
            {(seesRoster
              ? data.participants
              : data.participants.filter((member) => member.id === data.organizer.id)
            ).map((member) => (
              <Link
                key={member.id}
                to={profilePath(member.id, user?.id)}
                className="card ev-roster__row"
              >
                <Avatar user={member} size={34} showOnline />
                <span className="ev-roster__name">{member.username}</span>
                {member.id === data.organizer.id && (
                  <span className="ev-roster__tag">{t.sorties.fiche.organise}</span>
                )}
              </Link>
            ))}
          </div>

          {/*
            Le rappel a qui s'est engage.

            Une ligne grise en bas de fiche, pas un avertissement : la
            regle existe pour que les organisateurs puissent compter sur
            leurs inscrits, pas pour menacer ceux qui s'inscrivent.
          */}
          {!data.isPast && !isOrganizer && data.myStatus === 'confirmed' && (
            <p className="ev-detail__engagement">
              {t.sorties.fiche.prevenir}
            </p>
          )}

          {!seesRoster && (
            <p className="ev-roster__locked">
              {t.sorties.fiche.autresApresAcceptation}
            </p>
          )}

          {isOrganizer && data.pendingParticipants.length > 0 && (
            <p className="ev-roster__locked">
              {t.sorties.fiche.demandesEnAttente(data.pendingParticipants.length)}
            </p>
          )}

          {notice && <p className="field__error">{notice}</p>}
        </div>
      )}
    </SubScreen>
  );
}

/** Une des quatre informations de la grille, sans cadre autour. */
/**
 * Le lieu, qui seul peut ne pas tenir.
 *
 * Les points de suspension disent qu'il manque quelque chose sans
 * jamais le donner : une adresse coupee a « Riad Dar Zaman, mé… » ne
 * permet ni d'y aller ni de la recopier. Elle s'ouvre donc en fenetre.
 *
 * La mesure decide de tout : tant que l'adresse tient, ce n'est qu'un
 * repere de plus, et rien n'invite a appuyer. C'est le debordement reel
 * — `scrollWidth` au-dela de `clientWidth` — qui la rend touchable et lui
 * donne la couleur du spot. Une largeur d'ecran qui change, un pivot du
 * telephone, et la mesure se refait.
 */
function FactLieu({
  icon,
  value,
  anchor,
  long,
  onLong,
  onOpen,
}: {
  icon: ReactNode;
  value: string;
  anchor: RefObject<HTMLButtonElement>;
  /** Trop longue pour sa demi-largeur : elle prend toute la ligne. */
  long: boolean;
  onLong: (long: boolean) => void;
  onOpen: () => void;
}) {
  /* Une autre adresse se remesure depuis la demi-largeur. */
  useLayoutEffect(() => onLong(false), [value, onLong]);

  useLayoutEffect(() => {
    /*
      Deja sur toute la ligne : on ne mesure plus. Depliee, elle tient
      toujours ; la remesurer la replierait, et la fiche sauterait d'une
      disposition a l'autre.
    */
    if (long) return;
    const el = anchor.current;
    if (!el) return;
    const mesurer = () => {
      if (el.scrollWidth > el.clientWidth + 1) onLong(true);
    };
    mesurer();
    window.addEventListener('resize', mesurer);
    return () => window.removeEventListener('resize', mesurer);
  }, [value, anchor, long, onLong]);

  return (
    <div className={`ev-fact${long ? ' ev-fact--entier' : ''}`}>
      {icon}
      <span className="ev-fact__text">
        <span className="ev-fact__label">{t.sorties.commun.lieu}</span>
        {/*
          Un bouton meme quand il ne sert a rien : garder le meme element
          d'un cas a l'autre evite que la mesure porte sur une balise qui
          vient d'etre remplacee.
        */}
        <button
          ref={anchor}
          type="button"
          className={`ev-fact__value ev-fact__lieu${long ? ' ev-fact__lieu--long' : ''}`}
          disabled={!long}
          onClick={onOpen}
        >
          {value}
        </button>
      </span>
    </div>
  );
}

function Fact({
  icon,
  label,
  value,
  entier = false,
  demi = false,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  /** Tout le texte, sur toute la largeur et sur plusieurs lignes s'il le faut. */
  entier?: boolean;
  /** Tout le texte, mais dans une demi-largeur : un voisin partage la ligne. */
  demi?: boolean;
}) {
  return (
    <div className={`ev-fact${entier ? ' ev-fact--entier' : ''}${demi ? ' ev-fact--demi' : ''}`}>
      {icon}
      <span className="ev-fact__text">
        <span className="ev-fact__label">{label}</span>
        <span className="ev-fact__value" title={typeof value === 'string' ? value : undefined}>
          {value}
        </span>
      </span>
    </div>
  );
}

/** Le libelle et l'action dependent entierement du statut de participation. */
function ParticipationButton({
  status,
  full,
  validation,
  disabled,
  onClick,
}: {
  status: SiroccoEvent['myStatus'];
  /** Toutes les places confirmees sont prises. */
  full: boolean;
  /**
   * L'organisateur valide les demandes.
   *
   * Sans validation — c'est toujours le cas d'une sortie spontanee —
   * l'inscription est immediate, et « Demander a participer » promettait
   * une attente qui n'existe pas.
   */
  validation: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  // Une demande en attente doit pouvoir se retirer : la laisser bloquee
  // enfermait celui qui a change d'avis.
  if (status === 'pending') {
    return (
      <Button variant="soft" disabled={disabled} onClick={onClick}>
        {t.sorties.fiche.annulerDemande}
      </Button>
    );
  }
  if (status === 'confirmed') {
    return (
      <Button variant="soft" disabled={disabled} onClick={onClick}>
        {t.sorties.fiche.meDesinscrire}
      </Button>
    );
  }
  /*
    Complet ne veut plus dire ferme.

    Le bouton disait « Complet » et ne repondait pas : il fallait
    revenir voir de temps en temps si quelqu'un s'etait desiste. On
    prend la demande quand meme — elle rejoint la file, dans l'ordre —
    et la place qui se libere revient au premier a l'avoir demandee.
  */
  if (full) {
    return (
      <Button variant="soft" disabled={disabled} onClick={onClick}>
        {t.sorties.fiche.listeAttente}
      </Button>
    );
  }
  return (
    <Button disabled={disabled} onClick={onClick}>
      {validation ? t.sorties.fiche.demanderAParticiper : t.sorties.fiche.participer}
    </Button>
  );
}
